import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { useLang } from "../lib/i18n";
import { useRegion } from "./RegionContext";

const AppDataContext = createContext(null);

export function AppDataProvider({ children }) {
  const { user, updateMe } = useAuth();
  const { lang, t } = useLang();
  const { region } = useRegion();
  const zones = region.zones;
  const zoneKey = `mm_zone_${region.slug}`;
  const [categories, setCategories] = useState([]);
  const [localZone, setLocalZone] = useState(() => localStorage.getItem(zoneKey) || "all");
  const [favorites, setFavorites] = useState(new Set());
  const [loginPrompt, setLoginPrompt] = useState(false);
  const [schemas, setSchemas] = useState({});
  const [adultGate, setAdultGate] = useState(null);

  useEffect(() => {
    api.get("/attribute-schemas").then((r) => setSchemas(r.data));
  }, []);

  const guardCategory = useCallback(
    (key, proceed) => {
      if (key !== "contactos" || user?.can_access_adult) return proceed();
      setAdultGate({ onPass: proceed });
    },
    [user]
  );
  const catKind = useCallback((key) => categories.find((c) => c.key === key)?.kind || "generic", [categories]);
  const optLabel = useCallback(
    (field, value) => {
      const k = `opt.${field}.${value}`;
      const s = t(k);
      return s === k ? (field === "languages" ? value.toUpperCase() : value) : s;
    },
    [t]
  );

  const adultKey = user ? `${user.user_id}:${user.is_adult}` : String(user);
  useEffect(() => {
    if (user === null) return;
    api.get("/categories").then((r) => setCategories(r.data));
  }, [adultKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) {
      setFavorites(new Set());
      return;
    }
    api.get("/me/favorite-ids").then((r) => setFavorites(new Set(r.data)));
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const zone = user ? user.zone : localZone;
  const setZone = useCallback(
    async (z) => {
      localStorage.setItem(zoneKey, z);
      setLocalZone(z);
      if (user) await updateMe({ zone: z });
    },
    [user, updateMe, zoneKey]
  );

  const requireLogin = useCallback(() => {
    if (user) return true;
    setLoginPrompt(true);
    return false;
  }, [user]);

  const toggleFavorite = useCallback(
    async (id) => {
      if (!requireLogin()) return;
      const isFav = favorites.has(id);
      setFavorites((prev) => {
        const next = new Set(prev);
        isFav ? next.delete(id) : next.add(id);
        return next;
      });
      try {
        await (isFav ? api.delete(`/favorites/${id}`) : api.post(`/favorites/${id}`));
        toast.success(t(isFav ? "fav.removed" : "fav.added"));
      } catch {
        setFavorites((prev) => {
          const next = new Set(prev);
          isFav ? next.add(id) : next.delete(id);
          return next;
        });
        toast.error(t("err.generic"));
      }
    },
    [favorites, requireLogin, t]
  );

  const catName = useCallback(
    (key) => {
      const c = categories.find((x) => x.key === key);
      return c ? (lang === "en" ? c.name_en : c.name_es) : key;
    },
    [categories, lang]
  );
  const zoneName = useCallback(
    (key) => {
      const z = zones.find((x) => x.key === (key || "all"));
      return z ? (lang === "en" && z.name_en) || z.name : key;
    },
    [zones, lang]
  );

  return (
    <AppDataContext.Provider
      value={{
        categories, zones, zone, setZone, favorites, toggleFavorite,
        loginPrompt, setLoginPrompt, requireLogin, catName, zoneName,
        schemas, adultGate, setAdultGate, guardCategory, catKind, optLabel,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
}

export const useAppData = () => useContext(AppDataContext);
